<template>
  <v-layout>
    <v-flex>
      <v-layout align-center>
        <h1 class="headline">Rate Types</h1>
        <v-spacer></v-spacer>
        <div>
          <v-container pt-0>
            <list-visible-columns-and-filter
              :visible-columns="visibleColumns"
              :selectable-headers="selectableHeaders"
              v-model="search"
              @change="onVisibleColumnsChange"
            ></list-visible-columns-and-filter>
          </v-container>
        </div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" fab dark color="primary" @click="createItem">
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Create New Rate Type</span>
        </v-tooltip>
      </v-layout>

      <v-card>
        <v-data-table
          :headers="filteredHeaders"
          :items="rateTypes.edges"
          :loading="$apollo.loading"
          v-bind="pagingOptions"
          :search="search"
          item-key="node.id"
        >
          <template v-slot:item.includePax="{ item }">
            <v-checkbox
              v-model="item.node.includePax"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.includeDays="{ item }">
            <v-checkbox
              v-model="item.node.includeDays"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.includePax="{ item }">
            <v-checkbox
              v-model="item.node.includePax"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.includeQuantity="{ item }">
            <v-checkbox
              v-model="item.node.includeQuantity"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.notInUse="{ item }">
            <v-checkbox
              v-model="item.node.notInUse"
              primary
              hide-details
              :disabled="true"
              class="ma-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              :hide-view="true"
              @deleteItem="deleteItem($event)"
              @editItem="editItem($event)"
            ></default-list-actions>
          </template>
        </v-data-table>
      </v-card>
      <rate-type-edit-dialog
        v-model="selectedRate"
        :show="rateEditorDialog"
        @close="rateEditorDialog = false"
        @input="onSave"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import {
  ALL_RATE_TYPES_SEARCH_QUERY,
  RATE_TYPE_DELETE_MUTATION,
  RATE_TYPE_UPDATE_MUTATION,
  RATE_TYPE_CREATE_MUTATION,
} from "../../../../constants/graphql";
import RateEditor from "./RateEditor";
import DefaultListActions from "../../shared/DefaultListActions";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import SimpleListMixin from "../../../../mixins/SimpleListMixin";
import RateTypeEditDialog from "./RateTypeEditDialog.vue";

export default {
  name: "RateTypesList",
  mixins: [SimpleListMixin],
  components: {
    DefaultListActions,
    RateEditor,
    ListVisibleColumnsAndFilter,
    RateTypeEditDialog,
  },

  data() {
    return {
      search: "",
      selectedRate: {},
      rateTypes: {},
      doSaveRate: false,
      rateEditorDialog: false,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "code",
        "name",
        "includePax",
        "includeDays",
        "actions",
      ]),
      headers: [
        {
          text: "Rate Code",
          value: "node.code",
          name: "code",
        },
        {
          text: "Name",
          value: "node.name",
          name: "name",
        },
        {
          text: "Inc Pax",
          value: "includePax",
          name: "includePax",
        },
        {
          text: "Inc Days",
          value: "includeDays",
          name: "includeDays",
        },
        {
          text: "Inc Quantity",
          value: "includeQuantity",
          name: "includeQuantity",
        },
        {
          text: "Not In Use",
          value: "notInUse",
          name: "notInUse",
        },
        {
          text: "Min Stay",
          value: "node.minStay",
          name: "minStay",
        },
        {
          text: "Max Stay",
          value: "node.maxStay",
          name: "maxStay",
        },
        {
          text: "Min Pax",
          value: "node.minPax",
          name: "minPax",
        },
        {
          text: "Max Pax",
          value: "node.maxPax",
          name: "maxPax",
        },

        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
    };
  },

  methods: {
    createItem() {
      this.selectedRate = {};
      this.rateEditorDialog = true;
    },
    editItem(item) {
      this.selectedRate = item.node;
      this.rateEditorDialog = true;
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: RATE_TYPE_DELETE_MUTATION,
          variables: {
            rateId: item.node.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Rate type removed." });

      if (this.$apollo.queries.rateTypes != null) {
        this.$apollo.queries.rateTypes.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.rateTypes != null) {
        this.$apollo.queries.rateTypes.refetch();
      }
      this.onResetSave();
      this.rateEditorDialog = false;
      this.selectedRate = {};
      let msg = context == "edit" ? "Rate type updated." : "Rate type created.";
      this.$store.dispatch("showAppSnackbar", { msg });
    },
    onResetSave() {
      this.doSaveRate = false;
    },
    closeDialog() {
      this.rateEditorDialog = false;
      this.selectedRate = {};
    },
    onSaveRate() {
      this.doSaveRate = true;
    },
    onSave(payload) {
      this.rateEditorDialog = false;
      const { id, ...input } = payload;
      const mutation = id
        ? RATE_TYPE_UPDATE_MUTATION
        : RATE_TYPE_CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation,
          variables: { id, input },
          refetchQueries: [
            {
              query: ALL_RATE_TYPES_SEARCH_QUERY,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Rate Type Saved.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    rateTypes: {
      query: ALL_RATE_TYPES_SEARCH_QUERY,
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {},
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/department-types/RateTypesList.vue